import { type ReactNode } from 'react';
import { Accordion } from 'components/DesignSystems/Accordion/Accordion';
import { getSymbolSvgUrl, UscgSymbol } from './utils/uscgSymbology';
import { FeatureSymbol } from './FeatureSymbol';
import type { Geometry } from 'geojson';
import { IconButton } from 'components/DesignSystems/buttons/IconButton';

export type FeatureItemProps = {
  internalFeature?: {
    featureId: string; // icsIncidentLocation.id or generated pendingGraphicId
    key: string;
    title: string;
    resourceType: UscgSymbol;
    properties: unknown;
    updatedAt?: string | null;
    geometry: Geometry;
  };
  externalFeature?: {
    featureId: string; // external feature OBJECTID or generated id
    symbol?: __esri.SymbolUnion | null;
  };
  title: ReactNode;
  onZoom: () => void | Promise<void>;
  children: ReactNode;
  onEdit?: (featureId: string) => Promise<void> | void;
  onDelete?: (featureId: string) => Promise<void> | void;
};

export function FeatureItem({
  internalFeature,
  externalFeature,
  title,
  onZoom,
  children,
  onEdit,
  onDelete,
}: FeatureItemProps) {
  const featureId = (internalFeature?.featureId ??
    externalFeature?.featureId) as string;

  const eventKey = `feature-${featureId}`;

  return (
    <li>
      <Accordion
        eventKey={eventKey}
        classes={{
          card: 'border-neutral-500',
          header: 'p-0',
          body: 'ps-4 py-2 d-grid gap-2',
        }}
        header={
          <div className="d-flex align-items-center justify-content-between p-0">
            <span>{title}</span>
            <div className="d-flex align-items-center gap-1">
              {internalFeature ? (
                <img
                  src={getSymbolSvgUrl(internalFeature.resourceType)}
                  alt=""
                  width={20}
                  height={20}
                />
              ) : (
                <FeatureSymbol symbol={externalFeature?.symbol} />
              )}
              <IconButton
                iconProps={{ iconName: 'map' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onZoom();
                }}
                aria-label="Zoom to map location"
                title="Zoom to map location"
              />
              {onEdit && (
                <IconButton
                  iconProps={{ iconName: 'edit' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(featureId);
                  }}
                  aria-label="Edit object"
                  title="Edit object"
                />
              )}
              {onDelete && (
                <IconButton
                  iconProps={{ iconName: 'delete' }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    onDelete(featureId);
                  }}
                  aria-label="Delete object"
                  title="Delete object"
                />
              )}
            </div>
          </div>
        }
        showIcon={true}
      >
        {children}
      </Accordion>
    </li>
  );
}
